// src/components/Teacher/ClassDetail.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, orderBy, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import CreateAssignmentModal from './CreateAssignmentModal';
import { Assignment } from '../../types/assignmentTypes';
import EditAssignmentModal from './EditAssignmentModal';
import Notification from '../Account/Notification';
import ConfirmModal from '../Account/ConfirmModal';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceHighest:     '#cfdef5',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
interface Student {
    id: string;
    displayName: string;
    email: string;
    storiesCount: number;
    lastActivity: Date | null;
}
 
interface ClassData {
    id: string;
    name: string;
    joinCode: string;
    teacherId: string;
    students: { [key: string]: boolean };
}
 
const ClassDetail: React.FC = () => {
    const navigate = useNavigate();
    const { classId } = useParams<{ classId: string }>();
    const { currentUser } = useAuth();
 
    const [classData, setClassData]             = useState<ClassData | null>(null);
    const [students, setStudents]               = useState<Student[]>([]);
    const [loading, setLoading]                 = useState(true);
    const [showCreateAssignment, setShowCreateAssignment] = useState(false);
    const [assignments, setAssignments]         = useState<Assignment[]>([]);
    const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
    const [activeTab, setActiveTab]             = useState<'students' | 'assignments'>('students');
    const [regeneratingCode, setRegeneratingCode] = useState(false);
    const [isMobile, setIsMobile]               = useState(window.innerWidth < 768);
 
    const [confirmDelete, setConfirmDelete] = useState<{
        show: boolean; assignment: Assignment | null;
    }>({ show: false, assignment: null });
 
    const [notification, setNotification] = useState<{
        show: boolean;
        type: 'code-regenerated' | 'assignment-deleted' | 'error';
        message?: string;
        assignmentTitle?: string;
        errorMessage?: string;
    }>({ show: false, type: 'error' });
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    // ── Helpers ───────────────────────────────────────────────────────────────
    const generateJoinCode = (): string => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let code = '';
        for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
        return code.toUpperCase();
    };
 
    const refetchAssignments = async () => {
        if (!classData?.id) return;
        try {
            const q = query(
                collection(db, 'assignments'),
                where('classId', '==', classData.id),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            setAssignments(snap.docs.map(d => ({ id: d.id, ...d.data() } as Assignment)));
        } catch (err) {
            console.error('Error refetching assignments:', err);
        }
    };
 
    const regenerateJoinCode = async () => {
        if (!classId || !classData) return;
        setRegeneratingCode(true);
        try {
            const newJoinCode = generateJoinCode();
            await updateDoc(doc(db, 'classes', classId), { joinCode: newJoinCode });
            setClassData({ ...classData, joinCode: newJoinCode });
            setNotification({ show: true, type: 'code-regenerated', message: `New join code: ${newJoinCode}` });
        } catch (err) {
            console.error('Error regenerating join code:', err);
            setNotification({ show: true, type: 'error', errorMessage: 'Failed to regenerate join code.' });
        } finally {
            setRegeneratingCode(false);
        }
    };
 
    const getRelativeTime = (date: Date | null): string => {
        if (!date) return 'Never';
        const diffMs   = Date.now() - date.getTime();
        const diffMins  = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays  = Math.floor(diffMs / 86400000);
        if (diffMins < 1)  return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return '1d ago';
        if (diffDays < 30)  return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };
 
    // ── Data loading ──────────────────────────────────────────────────────────
    useEffect(() => {
        const loadClassData = async () => {
            if (!currentUser || !classId) return;
            try {
                const classDoc = await getDoc(doc(db, 'classes', classId));
                if (!classDoc.exists()) { alert('Class not found'); navigate('/dashboard'); return; }
 
                const classInfo = { id: classDoc.id, ...classDoc.data() } as ClassData;
                if (classInfo.teacherId !== currentUser.uid) { alert('Access denied'); navigate('/dashboard'); return; }
                setClassData(classInfo);
 
                // Students
                const studentIds = Object.keys(classInfo.students || {});
                const studentPromises = studentIds.map(async (sid) => {
                    const userDoc = await getDoc(doc(db, 'users', sid));
                    if (!userDoc.exists()) return null;
                    const ud = userDoc.data();
                    const storiesSnap = await getDocs(query(
                      collection(db, 'stories'),
                      where('userId', '==', sid),
                      where('classId', '==', classId),
                    ));
                    let lastActivity: Date | null = null;
                    storiesSnap.forEach(d => {
                        const date = d.data().lastEditedAt?.toDate() || d.data().createdAt?.toDate();
                        if (date && (!lastActivity || date > lastActivity)) lastActivity = date;
                    });
                    return { id: sid, displayName: ud.displayName || 'Student', email: ud.email || '', storiesCount: storiesSnap.size, lastActivity };
                });
                setStudents((await Promise.all(studentPromises)).filter(Boolean) as Student[]);
 
                // Assignments
const aSnap = await getDocs(query(
  collection(db, 'assignments'),
  where('classId', '==', classId),
  where('status', '==', 'active')
));
const loadedAssignments = aSnap.docs.map(d => ({ id: d.id, ...d.data() } as Assignment));

// Count submissions for each assignment from studentAssignments
const assignmentsWithCounts = await Promise.all(loadedAssignments.map(async (assignment) => {
  let submissionCount = 0;
  for (const sid of studentIds) {
      const statusDoc = await getDoc(doc(db, 'studentAssignments', `${sid}_${assignment.id}`));
      if (statusDoc.exists()) {
          const status = statusDoc.data().status;
          if (status === 'submitted' || status === 'returned') submissionCount++;
      }
  }
  return { ...assignment, submissionCount };
}));
setAssignments(assignmentsWithCounts);
 
            } catch (err) {
                console.error('Error loading class:', err);
            } finally {
                setLoading(false);
            }
        };
        loadClassData();
    }, [currentUser, classId, navigate]);
 
    const confirmDeleteAssignment = async () => {
        if (!confirmDelete.assignment) return;
        try {
            await updateDoc(doc(db, 'assignments', confirmDelete.assignment.id!), { status: 'archived' });
            setAssignments(assignments.filter(a => a.id !== confirmDelete.assignment!.id));
            setNotification({ show: true, type: 'assignment-deleted', assignmentTitle: confirmDelete.assignment.title });
            setConfirmDelete({ show: false, assignment: null });
        } catch (err) {
            console.error('Error deleting assignment:', err);
            setNotification({ show: true, type: 'error', errorMessage: 'Failed to delete assignment.' });
        }
    };
 
    // ── Loading state ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📚</div>
                    <div style={{ fontSize: '1rem', color: C.onSurfaceVariant, fontWeight: '600' }}>Loading class...</div>
                </div>
            </div>
        );
    }
 
    if (!classData) return null;
 
    // ── Tab button helper ─────────────────────────────────────────────────────
    const TabBtn: React.FC<{ id: 'students' | 'assignments'; icon: string; label: string; count: number }> = ({ id, icon, label, count }) => (
        <button
            onClick={() => setActiveTab(id)}
            style={{
                padding: '12px 20px', background: 'none', border: 'none',
                borderBottom: `3px solid ${activeTab === id ? C.primary : 'transparent'}`,
                color: activeTab === id ? C.primary : C.onSurfaceVariant,
                fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer',
                fontFamily: "'Epilogue', sans-serif", transition: 'color 0.15s',
                display: 'flex', alignItems: 'center', gap: '6px',
            }}
        >
            {icon} {label}
            <span style={{ background: activeTab === id ? C.primaryContainer : C.surfaceHigh, color: activeTab === id ? C.primary : C.onSurfaceVariant, padding: '1px 8px', borderRadius: '9999px', fontSize: '0.72rem', fontWeight: '800' }}>
                {count}
            </span>
        </button>
    );
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{
                marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
                padding: isMobile ? '20px 16px 80px' : '40px 48px',
                boxSizing: 'border-box', overflowX: 'hidden',
            }}>
 
                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{ marginBottom: '32px' }}>
                {/* ── Breadcrumb ───────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/dashboard')}
                        style={{ background: 'none', border: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                        Dashboard
                    </button>
                    <span style={{ color: C.outlineVariant, fontSize: '0.82rem' }}>›</span>
                    <span style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, fontWeight: '600' }}>{classData.name}</span>
                </div>
 
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.8rem' : '2.2rem', fontWeight: '900', color: C.onBackground, margin: '0 0 10px 0' }}>
                                {classData.name}
                            </h1>
 
                            {/* Join code */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, fontWeight: '600' }}>Join code:</span>
                                <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '900', letterSpacing: '2px', background: C.primaryContainer, color: C.primary, padding: '4px 12px', borderRadius: '8px' }}>
                                    {classData.joinCode}
                                </span>
                                <button
                                    onClick={() => navigator.clipboard.writeText(classData.joinCode)}
                                    style={{ background: 'none', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', padding: '4px 12px', fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                                >
                                    📋 Copy
                                </button>
                                <button
                                    onClick={regenerateJoinCode}
                                    disabled={regeneratingCode}
                                    style={{ background: 'none', border: 'none', color: regeneratingCode ? C.outlineVariant : C.primary, fontSize: '0.78rem', fontWeight: '700', cursor: regeneratingCode ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", textDecoration: 'underline' }}
                                >
                                    {regeneratingCode ? 'Regenerating...' : 'Regenerate'}
                                </button>
                            </div>
                        </div>
 
                        <button
                            onClick={() => setShowCreateAssignment(true)}
                            style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '12px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.primary}40`, whiteSpace: 'nowrap', transition: 'opacity 0.15s, transform 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            📝 Create Assignment
                        </button>
                    </div>
                </div>
 
                {/* ── Tabs ────────────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '4px', borderBottom: `2px solid ${C.surfaceHigh}`, marginBottom: '24px' }}>
                    <TabBtn id="students"    icon="👥" label="Students"    count={students.length} />
                    <TabBtn id="assignments" icon="📝" label="Assignments" count={assignments.length} />
                </div>
 
                {/* ── Students tab ─────────────────────────────────────────── */}
                {activeTab === 'students' && (
                    <div style={{ background: C.surfaceLowest, borderRadius: '20px', overflow: 'hidden', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        {students.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 32px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '14px' }}>👥</div>
                                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>No students yet</h3>
                                <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>
                                    Share your join code <strong style={{ color: C.primary, fontFamily: 'monospace' }}>{classData.joinCode}</strong> with students to get started!
                                </p>
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: `2px solid ${C.surfaceHigh}`, background: C.surfaceLow }}>
                                        {['Student', 'Stories', 'Last Active', ''].map((h, i) => (
                                            <th key={i} style={{ padding: '14px 20px', textAlign: i === 0 ? 'left' : i === 3 ? 'right' : 'center', fontSize: '0.72rem', fontWeight: '800', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'Epilogue', sans-serif" }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map(student => (
                                        <tr
                                            key={student.id}
                                            style={{ borderBottom: `1px solid ${C.surfaceHigh}`, transition: 'background 0.15s' }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = C.surfaceLow; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                        >
                                            <td style={{ padding: '16px 20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '800', fontSize: '0.95rem', flexShrink: 0 }}>
                                                        {student.displayName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span style={{ fontWeight: '700', fontSize: '0.9rem', color: C.onBackground }}>{student.displayName}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                                <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.1rem', color: C.primary }}>{student.storiesCount}</span>
                                            </td>
                                            <td style={{ padding: '16px 20px', textAlign: 'center', fontSize: '0.85rem', color: C.onSurfaceVariant, fontWeight: '500' }}>
                                                {getRelativeTime(student.lastActivity)}
                                            </td>
                                            <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => navigate(`/teacher/class/${classId}/student/${student.id}`)}
                                                    style={{ background: C.primaryContainer, color: C.primary, border: 'none', borderRadius: '9999px', padding: '7px 16px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = C.primaryContainer; e.currentTarget.style.color = C.primary; }}
                                                >
                                                    View Work →
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
 
                {/* ── Assignments tab ──────────────────────────────────────── */}
                {activeTab === 'assignments' && (
                    <div>
                        {assignments.length === 0 ? (
                            <div style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '60px 32px', textAlign: 'center', border: `1px solid ${C.surfaceHigh}` }}>
                                <div style={{ fontSize: '3rem', marginBottom: '14px' }}>📝</div>
                                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>No assignments yet</h3>
                                <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>Click "Create Assignment" above to get started.</p>
                                <button
                                    onClick={() => setShowCreateAssignment(true)}
                                    style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '11px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                                >
                                    📝 Create First Assignment
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {assignments.map(assignment => (
                                    <div
                                        key={assignment.id}
                                        onClick={() => navigate(`/teacher/class/${classId}/assignment/${assignment.id}`)}
                                        style={{ background: C.surfaceLowest, borderRadius: '18px', padding: '20px 24px', border: `1px solid ${C.surfaceHigh}`, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 6px rgba(0,0,0,0.04)'; }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, margin: '0 0 4px 0' }}>
                                                    {assignment.title}
                                                </h3>
                                                <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0, lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {assignment.description}
                                                </p>
                                                {assignment.prompt && (
                                                    <div style={{ background: C.primaryContainer, padding: '8px 12px', borderRadius: '8px', fontSize: '0.8rem', color: C.primary, marginTop: '8px' }}>
                                                        <strong>Prompt:</strong> {assignment.prompt}
                                                    </div>
                                                )}
                                            </div>
 
                                            {/* Actions */}
                                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                                <button
                                                    onClick={e => { e.stopPropagation(); setEditingAssignment(assignment); }}
                                                    style={{ background: C.primaryContainer, color: C.primary, border: 'none', borderRadius: '9999px', padding: '7px 14px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = C.primaryContainer; e.currentTarget.style.color = C.primary; }}
                                                >
                                                    ✏️ Edit
                                                </button>
                                                <button
                                                    onClick={e => { e.stopPropagation(); setConfirmDelete({ show: true, assignment }); }}
                                                    style={{ background: C.errorContainer, color: C.error, border: 'none', borderRadius: '9999px', padding: '7px 14px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = C.error; e.currentTarget.style.color = '#fff'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = C.errorContainer; e.currentTarget.style.color = C.error; }}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
 
                                        {/* Meta row */}
                                        <div style={{ display: 'flex', gap: '20px', fontSize: '0.8rem', color: C.onSurfaceVariant, paddingTop: '12px', borderTop: `1px solid ${C.surfaceHigh}`, flexWrap: 'wrap' }}>
                                            <span>📅 Due {assignment.dueDate.toDate().toLocaleDateString()} at {assignment.dueDate.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            {assignment.minWords && (
                                                <span>✍️ {assignment.minWords}{assignment.maxWords ? `–${assignment.maxWords}` : '+'} words</span>
                                            )}
                                            <span style={{ color: C.secondary, fontWeight: '700' }}>
                                                ✅ {assignment.submissionCount || 0} / {students.length} submitted
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
 
                {/* ── Tip ─────────────────────────────────────────────────── */}
                <div style={{ marginTop: '24px', padding: '14px 18px', background: C.primaryContainer, borderRadius: '12px', fontSize: '0.82rem', color: C.primary, fontWeight: '600' }}>
                    💡 Click "View Work" to see a student's submissions and provide feedback.
                </div>
            </div>
 
            {/* ── Modals ───────────────────────────────────────────────────── */}
            {showCreateAssignment && (
                <CreateAssignmentModal
                    isOpen={showCreateAssignment}
                    onClose={() => setShowCreateAssignment(false)}
                    classId={classData!.id}
                    className={classData!.name}
                    onAssignmentCreated={refetchAssignments}
                />
            )}
 
            {editingAssignment && (
                <EditAssignmentModal
                    isOpen={!!editingAssignment}
                    onClose={() => setEditingAssignment(null)}
                    assignment={editingAssignment}
                    onAssignmentUpdated={() => {}}
                />
            )}
 
            <ConfirmModal
                isOpen={confirmDelete.show}
                onClose={() => setConfirmDelete({ show: false, assignment: null })}
                onConfirm={confirmDeleteAssignment}
                title="Delete Assignment?"
                message={`Are you sure you want to delete "${confirmDelete.assignment?.title}"?\n\nThis will NOT delete student stories, but students will no longer see this assignment in their to-do list.`}
                confirmText="Yes, Delete"
                cancelText="Cancel"
                emoji="🗑️"
                danger={true}
            />
 
            <Notification
                isOpen={notification.show}
                onClose={() => setNotification({ ...notification, show: false })}
                type={notification.type}
                assignmentTitle={notification.assignmentTitle}
                errorMessage={notification.errorMessage}
            />
        </div>
    );
};
 
export default ClassDetail;